import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useStore } from '@/utils/store';
import { format, subMonths, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

export const selectMonthlyData = (state) => {
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const monthDate = subMonths(now, i);
    const start = startOfMonth(monthDate);
    const end = endOfMonth(monthDate);
    const monthTxs = state.transactions.filter((t) => {
      const d = new Date(t.date + 'T00:00:00');
      return isWithinInterval(d, { start, end });
    });
    months.push({
      month: monthDate,
      income: monthTxs.filter((t) => t.type === 'income').reduce((s, t) => s + t.amount, 0),
      expenses: monthTxs.filter((t) => t.type === 'expense').reduce((s, t) => s + t.amount, 0),
    });
  }
  return months;
};

export default function MonthlyOverviewChart() {
  const { t, i18n } = useTranslation();
  const monthlyData = useStore(selectMonthlyData);
  const locale = i18n.language === 'es' ? es : enUS;

  const data = useMemo(() =>
    monthlyData.map((d) => ({
      name: format(d.month, 'MMM', { locale }),
      [t('dashboard.income')]: d.income,
      [t('dashboard.expenses')]: d.expenses,
    })),
    [monthlyData, locale, t]
  );

  return (
    <div className="card">
      <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
        {t('dashboard.monthlyOverview')}
      </h2>
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="expenseGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#9ca3af" />
          <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--tw-bg-opacity, #fff)',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              fontSize: '12px',
            }}
          />
          <Area
            type="monotone"
            dataKey={t('dashboard.income')}
            stroke="#22c55e"
            fill="url(#incomeGrad)"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            dataKey={t('dashboard.expenses')}
            stroke="#ef4444"
            fill="url(#expenseGrad)"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
